import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as api from "../api";
import ArmViewer from "./ArmViewer";
import type { Counterexample, RuleTemplate } from "../api";

/**
 * Author a restriction, see what it forbids, then approve it.
 *
 * Two things make this different from a settings form, and both are about
 * what the person is told before they commit:
 *
 * 1. The channel is shown with the rule, not after it. A restriction that we
 *    merely watch for must never be presented as one that stops the arm.
 * 2. Approval is gated on a counterexample. A paraphrase — "joint 2 limited
 *    to -45..90 degrees" — is something people nod at without being able to
 *    tell whether it means what they wanted. A motion they can watch is
 *    something they can reject in a second.
 */

const CHANNEL_A = "A_enforced_by_robot";

const DEFAULTS: Record<string, Record<string, number>> = {
  joint_range: { joint_index: 1, min_deg: -45, max_deg: 90 },
  workspace_box: {
    x_min: -0.6,
    x_max: 0.6,
    y_min: -0.6,
    y_max: 0.6,
    z_min: 0,
    z_max: 0.6,
  },
  avoid_zone: { radius_m: 0.4 },
  time_window: { start_hour: 22, end_hour: 6 },
};

export default function RuleAuthor() {
  // The route is /projects/:id/rules, so the param is `id`.
  const { id: projectId } = useParams<{ id: string }>();

  const [templates, setTemplates] = useState<RuleTemplate[]>([]);
  const [selected, setSelected] = useState<RuleTemplate | null>(null);
  const [params, setParams] = useState<Record<string, number>>({});
  const [example, setExample] = useState<Counterexample | null>(null);
  const [frame, setFrame] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authorised, setAuthorised] = useState<string | null>(null);

  useEffect(() => {
    api
      .listRuleTemplates()
      .then((r) => setTemplates(r.templates))
      .catch((e) => setError((e as Error).message));
  }, []);

  const choose = useCallback((template: RuleTemplate) => {
    setSelected(template);
    setParams(DEFAULTS[template.key] ?? {});
    setExample(null);
    setAuthorised(null);
    setError(null);
  }, []);

  const preview = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.ruleCounterexample(selected.key, params);
      setExample(result.counterexample);
      setFrame(result.counterexample.violates_at ?? 0);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [selected, params]);

  const approve = useCallback(async () => {
    if (!projectId || !selected || !example) return;
    setLoading(true);
    setError(null);
    try {
      const entry = await api.authoriseRule(projectId, {
        template_key: selected.key,
        channel: selected.channel,
        parameters: params,
        enforcement: selected.enforcement,
        counterexample: example,
        counterexample_shown: true,
      });
      setAuthorised(entry.entry_hash);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [projectId, selected, example, params]);

  const enforced = selected?.channel === CHANNEL_A;

  return (
    <div className="p-4">
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
        Restrictions
      </h1>
      <p className="text-sm text-muted" style={{ marginBottom: 20, maxWidth: 620 }}>
        Set a limit, watch what it forbids, then approve it. Your approval is
        signed against your name along with the motion you were shown.
      </p>

      {error && (
        <div className="card mb-4" style={{ borderColor: "var(--color-danger)" }}>
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="card mb-4">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
          1. Choose a restriction
        </h2>
        <div style={{ display: "grid", gap: 8 }}>
          {templates.map((t) => {
            const isA = t.channel === CHANNEL_A;
            return (
              <button
                key={t.key}
                className="card"
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  borderColor:
                    selected?.key === t.key
                      ? "var(--color-primary)"
                      : "var(--color-border)",
                }}
                onClick={() => choose(t)}
              >
                <div className="flex items-center gap-2">
                  <strong style={{ fontSize: 14 }}>{t.label}</strong>
                  {/* The channel travels with the rule everywhere it is shown. */}
                  <span
                    className="text-sm"
                    style={{
                      padding: "1px 6px",
                      borderRadius: 4,
                      border: "1px solid var(--color-border)",
                      color: isA
                        ? "var(--color-success)"
                        : "var(--color-text-secondary)",
                    }}
                  >
                    {isA ? "Robot enforces this" : "We watch — does not stop the arm"}
                  </span>
                </div>
                <div className="text-sm text-muted" style={{ marginTop: 4 }}>
                  {t.description}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="card mb-4">
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            2. Set the limit
          </h2>
          <p
            className="text-sm"
            style={{
              marginBottom: 12,
              color: enforced
                ? "var(--color-text-secondary)"
                : "var(--color-warning)",
            }}
          >
            {selected.enforcement}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {selected.parameters.map((key) => (
              <div key={key} className="form-group">
                <label className="form-label" htmlFor={`param-${key}`}>
                  {key.replace(/_/g, " ")}
                </label>
                <input
                  id={`param-${key}`}
                  className="form-input"
                  type="number"
                  step="0.1"
                  value={params[key] ?? 0}
                  onChange={(e) =>
                    setParams((prev) => ({
                      ...prev,
                      [key]: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            ))}
          </div>

          <button
            className="btn btn-primary mt-2"
            disabled={loading || !selected.counterexample_available}
            onClick={preview}
          >
            {loading ? "Working…" : "Show me what this forbids"}
          </button>

          {!selected.counterexample_available && (
            <p className="text-sm text-muted" style={{ marginTop: 8 }}>
              This restriction has no geometric shape we can draw, so there is
              no motion to show you. It will be recorded and reported, and it
              will not stop the arm.
            </p>
          )}
        </div>
      )}

      {example && (
        <div className="card mb-4">
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            3. Watch what it forbids
          </h2>
          <p className="text-sm" style={{ marginBottom: 12 }}>
            {example.explanation}
          </p>

          {example.found ? (
            <>
              <ArmViewer
                robotModel={example.robot_model}
                frames={example.frames}
                frame={frame}
                violatesAt={example.violates_at}
                tcpPath={example.tcp_path}
                box={
                  selected?.key === "workspace_box"
                    ? ["x_min", "x_max", "y_min", "y_max", "z_min", "z_max"].map(
                        (k) => params[k] ?? 0,
                      )
                    : null
                }
                highlightJoint={
                  selected?.key === "joint_range" ? (params.joint_index ?? 0) + 1 : null
                }
              />
              <input
                type="range"
                min={0}
                max={example.frames.length - 1}
                value={frame}
                onChange={(e) => setFrame(parseInt(e.target.value, 10))}
                style={{ width: "100%" }}
              />
              <div className="text-sm text-muted" style={{ marginBottom: 8 }}>
                Frame {frame + 1} of {example.frames.length}
                {example.violates_at !== null && frame >= example.violates_at && (
                  <span style={{ color: "var(--color-danger)", marginLeft: 8 }}>
                    forbidden by your rule
                  </span>
                )}
              </div>
              <div className="text-sm" style={{ fontFamily: "monospace" }}>
                tool at{" "}
                {example.tcp_path[frame]
                  ?.map((v) => v.toFixed(3))
                  .join(", ")}{" "}
                m
              </div>

              <button
                className="btn btn-primary mt-4"
                disabled={loading || authorised !== null}
                onClick={approve}
              >
                {authorised
                  ? "Approved and signed"
                  : "This is right — approve it"}
              </button>
            </>
          ) : (
            <p className="text-sm text-muted">
              Nothing to approve yet: without a forbidden motion to show you,
              we will not record an approval.
            </p>
          )}

          {authorised && (
            <p className="text-sm text-muted" style={{ marginTop: 8 }}>
              Signed into your ledger as{" "}
              <code>{authorised.slice(0, 16)}…</code>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
